import { useState } from 'react'
import { checkPurchaseLink, hostOf, safeLinks } from '@kronos/domain'
import type { PurchaseLink } from '@kronos/domain'

/**
 * Where to buy a bought part — the buttons, and the editor behind them.
 *
 * The point of these is the *moment*: you open the shelf count, see you are
 * low, and the page that sells it is one tap away rather than a search somebody
 * has already done four times. So they appear on Stock, where you notice, and
 * not only in Catalog, where you file them.
 *
 * **Every href goes through `safeLinks` first**, even though the server already
 * filtered on the way in and out. A `javascript:` URL in an href runs on click
 * with the session of whoever clicked it, and "some other layer checked" is how
 * that ends up shipped. The cost of checking again is one pass over four items.
 */

/** `noopener` is not optional: without it the page you open gets a handle on
 *  this one through `window.opener` and can navigate it somewhere else. */
const REL = 'noopener noreferrer'

export function BuyLinks({
  links,
  compact = false,
}: {
  links: readonly PurchaseLink[]
  compact?: boolean
}): JSX.Element | null {
  const safe = safeLinks(links)
  if (safe.length === 0) return null

  return (
    <div className="buy-links">
      {safe.map((link) => (
        <a
          key={link.url}
          className={`buy-link ${compact ? 'buy-link--compact' : ''}`}
          href={link.url}
          target="_blank"
          rel={REL}
          // Stop a card that is itself a button from swallowing the click.
          onClick={(e) => e.stopPropagation()}
          title={link.url}
        >
          <span aria-hidden="true">↗</span>
          {link.label}
          {compact ? null : <span className="buy-link__host">{hostOf(link.url)}</span>}
        </a>
      ))}
    </div>
  )
}

/**
 * Adding one at a time, like the counter chips and for the same reason: a
 * textarea of URLs hides a typo until somebody clicks it and lands nowhere.
 */
export function BuyLinkEditor({
  links,
  onChange,
}: {
  links: readonly PurchaseLink[]
  onChange: (next: readonly PurchaseLink[]) => void
}): JSX.Element {
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  const add = (): void => {
    if (url.trim() === '') return
    const checked = checkPurchaseLink({ label, url }, links)
    if (!checked.ok) {
      setProblem(checked.detail)
      return
    }
    onChange([...links, checked.link])
    setUrl('')
    setLabel('')
    setProblem(null)
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      {links.length > 0 ? (
        <div className="stack" style={{ gap: 6 }}>
          {links.map((link) => (
            <div key={link.url} className="buy-row">
              <span className="buy-row__label">{link.label}</span>
              <span className="buy-row__url muted">{link.url}</span>
              <button
                type="button"
                aria-label={`Remove ${link.label}`}
                onClick={() => onChange(links.filter((l) => l.url !== link.url))}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="buy-add">
        <input
          value={url}
          placeholder="https://…"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => {
            setUrl(e.target.value)
            setProblem(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
        />
        <input
          value={label}
          placeholder="name it (optional)"
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
        />
        <button type="button" onClick={add} disabled={url.trim() === ''}>
          Add
        </button>
      </div>
      {problem ? <span className="critical field__note">{problem}</span> : null}
      <span className="muted field__note">
        Leave the name blank and it uses the site — “mcmaster.com”.
      </span>
    </div>
  )
}
